import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigation } from '@/contexts/NavigationContext';
import { UserLevel } from '@/types/auth.types';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PatientForm } from '@/components/forms/PatientForm';
import { AddPatientWizard } from '@/components/forms/AddPatientWizard';
import { UserPlus, Maximize2, Minimize2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// Import dashboards
import { MaternityDashboard } from '@/pages/MaternityDashboard';
import { HospitalDashboard } from '@/pages/HospitalDashboard';
import { AdministratorDashboard } from '@/pages/AdministratorDashboard';
import { Dashboard } from '@/pages/Dashboard';
import { PrematuriDashboard } from '@/pages/PrematuriDashboard';
import { CHDDashboard } from '@/pages/CHDDashboard';
import { PatientsPage } from '@/pages/PatientsPage';
import { PrematurePatientsPage } from '@/pages/PrematurePatientsPage';
import { ProceduresPage } from '@/pages/ProceduresPage';
import { DiagnosticsPage } from '@/pages/DiagnosticsPage';
import { StatisticsPage } from '@/pages/StatisticsPage';

// Import Report Pages
import { ClinicalReportsPage } from '@/pages/ClinicalReportsPage';
import { HospitalReportsPage } from '@/pages/HospitalReportsPage';

// Import components
import { UsersManagement } from '@/components/admin/UsersManagement';
import { HospitalsManagement } from '@/components/admin/HospitalsManagement';
import { ProcedureTypesManagement } from '@/components/admin/ProcedureTypesManagement';
import { CHDCodesManagement } from '@/components/admin/CHDCodesManagement';
import { EurocatReports } from '@/components/admin/EurocatReports';
import { SystemAnalytics } from '@/components/admin/SystemAnalytics';
import { BackupRestore } from '@/components/admin/BackupRestore';
import { AuditTrail } from '@/components/admin/AuditTrail';
import { SystemSettings } from '@/components/admin/SystemSettings';
import { SystemAlerts } from '@/components/admin/SystemAlerts';
import { UserProfile } from '@/components/profile/UserProfile';
import { HelpCenter } from '@/components/common/HelpCenter';

export const AppContent: React.FC = () => {
  const { utilizator } = useAuth();
  const { currentPage, navigate } = useNavigation();
  const [isNewCaseModalOpen, setIsNewCaseModalOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  // Global listener for new-case modal - stays active regardless of current page
  useEffect(() => {
    const handleOpenNewCaseModal = () => {
      setIsNewCaseModalOpen(true);
    };

    window.addEventListener('open-new-case-modal', handleOpenNewCaseModal);
    return () => {
      window.removeEventListener('open-new-case-modal', handleOpenNewCaseModal);
    };
  }, []);

  if (!utilizator) {
    return null;
  }

  // Render component based on current page
  const renderPage = () => {
    switch (currentPage) {
      // Dashboard pages based on user level
      case 'maternity-dashboard':
        return <MaternityDashboard />;
      case 'hospital-dashboard':
        return <HospitalDashboard />;
      case 'admin-dashboard':
        return <AdministratorDashboard />;
      // Section-specific dashboards for hospital users
      case 'prematuri-dashboard':
        return <PrematuriDashboard />;
      case 'chd-dashboard':
        return <CHDDashboard />;
      case 'dashboard':
        // Fallback to role-specific dashboard
        const userLevel = utilizator.user_level_id || utilizator.user_level?.id || 0;
        switch (userLevel) {
          case UserLevel.MATERNITY:
            return <MaternityDashboard />;
          case UserLevel.HOSPITAL:
            return <HospitalDashboard />;
          case UserLevel.ADMINISTRATOR:
            return <AdministratorDashboard />;
          default:
            return <Dashboard />;
        }

      // Maternity pages - new-case is now handled as modal in MaternityDashboard

      // Hospital pages
      case 'patients':
        return <PatientsPage />;
      case 'premature-patients':
        return <PrematurePatientsPage />;
      case 'procedures':
        return <ProceduresPage />;
      case 'diagnoses':
        return <DiagnosticsPage />;
      case 'clinical-reports':
        return <ClinicalReportsPage />;
      case 'reports-hospital':
        return <HospitalReportsPage />;
      case 'emergencies':
        return (
          <div className="p-6">
            <h1 className="text-2xl font-bold">Urgențe</h1>
            <p className="text-muted-foreground mt-2">Această pagină va fi implementată în curând.</p>
          </div>
        );
      case 'statistics':
        return <StatisticsPage />;

      // Administrator pages
      case 'users':
        return <UsersManagement />;
      case 'hospitals':
        return <HospitalsManagement />;
      case 'procedure-types':
        return <ProcedureTypesManagement />;
      case 'chd-codes':
        return <CHDCodesManagement />;
      case 'eurocat-reports':
        return <EurocatReports />;
      case 'system-analytics':
        return <SystemAnalytics />;
      case 'audit':
        return <AuditTrail />;
      case 'backup':
        return <BackupRestore />;
      case 'settings':
        return <SystemSettings />;
      case 'alerts':
        return <SystemAlerts />;

      // Common pages
      case 'profile':
        return <UserProfile />;
      case 'reports':
        return (
          <div className="p-6">
            <h1 className="text-2xl font-bold">Rapoarte</h1>
            <p className="text-muted-foreground mt-2">Această pagină va fi implementată în curând.</p>
          </div>
        );
      case 'help':
        return <HelpCenter />;

      default: {
        // Default to user-specific dashboard
        const userLevel = utilizator.user_level_id || utilizator.user_level?.id || 0;
        switch (userLevel) {
          case UserLevel.MATERNITY:
            return <MaternityDashboard />;
          case UserLevel.HOSPITAL:
            return <HospitalDashboard />;
          case UserLevel.ADMINISTRATOR:
            return <AdministratorDashboard />;
          default:
            return <Dashboard />;
        }
      }
    }
  };

  return (
    <>
      {renderPage()}

      {/* Global New Case Modal - accessible from any page */}
      <Dialog open={isNewCaseModalOpen} onOpenChange={(open) => {
        setIsNewCaseModalOpen(open);
        if (!open) setIsMaximized(false); // Reset maximize state when closing
      }}>
        <DialogContent
          hideClose
          className={cn(
            isMaximized
              ? "w-screen h-screen max-w-none max-h-none overflow-y-auto flex flex-col"
              : "w-[90vw] max-w-6xl max-h-[90vh] overflow-y-auto"
          )}
        >
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                <UserPlus className="h-6 w-6" />
                Înregistrare caz nou
              </DialogTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setIsMaximized(!isMaximized)}
                  className="h-8 w-8"
                >
                  {isMaximized ? (
                    <Minimize2 className="h-4 w-4" />
                  ) : (
                    <Maximize2 className="h-4 w-4" />
                  )}
                </Button>
                <DialogClose asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Închide</span>
                  </Button>
                </DialogClose>
              </div>
            </div>
            <DialogDescription>
              Completați formularul pentru a înregistra un nou caz MCC
            </DialogDescription>
          </DialogHeader>
          <div className={cn("mt-6", isMaximized && "flex-1")}>
            <AddPatientWizard
              onClose={() => setIsNewCaseModalOpen(false)}
              onComplete={(patient) => {
                setIsNewCaseModalOpen(false);
                // Dispatch event to refresh dashboard data
                window.dispatchEvent(new CustomEvent('patient-created'));
              }}
              onTakeoverRequest={
                (utilizator.user_level_id || utilizator.user_level?.id) === UserLevel.HOSPITAL
                  ? (cnp) => {
                    setIsNewCaseModalOpen(false);
                    sessionStorage.setItem('pendingPatientTakeoverCnp', cnp);
                    navigate('patients');
                  }
                  : undefined
              }
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
